import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { BsInfoCircle } from 'react-icons/bs';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { Group, GroupService, InvitationRead, InvitationService } from '../../api';
import { FormTextField } from '../../app/components/FormTextField';
import { groupQueryKeys } from '../../app/query-keys-factories/groups';
import { invitationQueryKeys } from '../../app/query-keys-factories/invitations';
import { handleApiError } from '../../app/utils/errorhandling';
import { setFormErrorsFromApiErrors } from '../../utils/forms';

const invitationValidation = z.object({
  email: z.string().email(),
  group_names: z.union([z.string().min(1), z.array(z.string()), z.boolean()]),
});

export const InviteUserDialog = ({ show, onClose }: { show: boolean; onClose: () => void }) => {
  const form = useForm({ resolver: zodResolver(invitationValidation) });
  const queryGroups = useQuery({
    queryKey: groupQueryKeys.list({}),
    queryFn: async () => {
      let groups: Group[] = [];
      const limit = 100;
      let totalCount: number;
      let offset = 0;

      // fetch all facilities considering that data.count is the total number of facilities
      let data = await GroupService.groupList({
        limit,
        offset,
      });
      totalCount = data.count as number;
      groups = groups.concat(data.results || []);
      while (groups.length < totalCount) {
        offset += limit;
        data = await GroupService.groupList({ limit, offset });
        totalCount = data.count as number;
        groups = groups.concat(data.results || []);
      }
      return groups;
    },
    keepPreviousData: true,
  });

  useEffect(() => {
    const DEFAULT_GROUPS = ['connect_with_providers', 'schedule_shifts'];
    const apiGroupNames = queryGroups.data?.map((group) => group.code);
    const preSelectedGroupNames = DEFAULT_GROUPS.filter((groupName) =>
      apiGroupNames?.includes(groupName)
    );
    if (preSelectedGroupNames.length > 0) {
      form.setValue('group_names', preSelectedGroupNames);
    }
  }, [queryGroups.data]);

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: InvitationRead) => {
      await InvitationService.invitationCreate({ requestBody: data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(invitationQueryKeys.all());
    },
  });

  const onSubmit = async (data) => {
    let submitData = data;
    if (typeof data.group_names === 'string') {
      submitData = { ...data, group_names: [data.group_names] };
    }
    if (typeof data.group_names === 'boolean') {
      submitData = { ...data, group_names: [] };
    }

    try {
      await mutation.mutateAsync(submitData);
      toast.success('User invited successfully.');
      form.setValue('email', '');
      onClose();
    } catch (errors) {
      setFormErrorsFromApiErrors(errors, form.setError);
      handleApiError(error);
    }
  };
  return (
    <Modal show={show} onHide={onClose}>
      <Form onSubmit={form.handleSubmit(onSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>Invite User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormTextField
            name="email"
            label="Email"
            required
            errors={form.formState.errors}
            register={form.register}
          />
          <div>
            <div className="mb-3">
              <p className="mt-1 bg-primary-muted border-1 p-2 text-muted small rounded fw-semibold tw:border-zinc-300">
                <BsInfoCircle className="tw:inline me-2" size="15px" />
                By default, every team member has access to view the marketplace and connected
                providers.
              </p>
              <p className="mb-0 fw-semibold">Set the appropriate User Permissions</p>
            </div>
            {queryGroups.isLoading && (
              <Row className="my-3">
                <Col xs={12} className="d-flex align-items-center justify-content-center">
                  <Spinner
                    aria-label="loading indicator"
                    animation="grow"
                    className="loading-spinner"
                    variant="primary"
                  />
                </Col>
              </Row>
            )}
            {queryGroups.isError && (
              <Row className="row-cols-auto justify-content-center">
                <Col>
                  <Alert variant="danger" show={queryGroups.isError}>
                    <Alert.Heading>Something went wrong</Alert.Heading>
                    <p>We couldn&apos;t retrieve the list of permissions.</p>
                  </Alert>
                </Col>
              </Row>
            )}
            {queryGroups.isSuccess && (
              <div>
                {queryGroups.data?.map((group) => (
                  <Form.Group controlId={`group_names-${group.code}`} key={group.code}>
                    <Form.Check
                      type="switch"
                      value={group.code}
                      label={group.title}
                      {...form.register('group_names')}
                    />
                  </Form.Group>
                ))}
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" type="submit">
            Invite
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
